import { useCallback, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { Trans } from '@lingui/macro'

import { LoadingButton } from '@mui/lab'
import { Alert, Button, Stack } from '@mui/material'

import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import { AlertsPortal } from '@/components/ui/alert/AlertsPortal'
import { InsIdentity } from '@/core/types/dmp-connect'
import { useAppSelector } from '@/redux/rootReducer'
import { selectVitaleCard } from '@/redux/reducers/dmp-connect.reducer'

import { DMPConnectVitaleCardPatient } from '../model/types'
import { useDMPConnectVitaleCardForm } from '../model/useDMPConnectVitaleCardForm'
import { DMP_CONNECT_VITALE_CARD_FORM_ALERTS } from '../model/useGetInsFromVitaleCard'
import { SelectCardReader } from './SelectCardReader'
import { VitaleCardPatientOption } from './VitaleCardPatientOption'

export type DMPConnectVitaleCardFormProps = {
  hideGetIns?: boolean
  onSelectPatient?: (patient: DMPConnectVitaleCardPatient) => void
  patientId?: string
}

export function DMPConnectVitaleCardForm({ hideGetIns, onSelectPatient, patientId }: DMPConnectVitaleCardFormProps) {
  const vitaleCard = useAppSelector(selectVitaleCard)
  const [patientInsMap, setPatientInsMap] = useState<Record<number, InsIdentity>>({})
  const [selectedPatientIndex, setSelectedPatientIndex] = useState<number>()

  const { isSameCard, isSubmitting, newCardInserted, noCardInserted, rhForm, submitHandler, vitaleCardPatients } = useDMPConnectVitaleCardForm()
  const cardChanged = newCardInserted && !isSameCard

  const onIdentity = useCallback((index: number, identity: InsIdentity) => {
    setPatientInsMap((previous) => ({ ...previous, [index]: identity }))
  }, [])

  const patientOptions = useMemo(() => {
    if (cardChanged || noCardInserted) return []
    return (
      (vitaleCardPatients ?? vitaleCard?.Patients)?.map((patient, index) => ({
        label: <VitaleCardPatientOption hideGetIns={hideGetIns} index={index} patient={patient} patientId={patientId} onIdentity={onIdentity} />,
        value: index,
      })) ?? []
    )
  }, [vitaleCard, vitaleCardPatients, cardChanged, noCardInserted, hideGetIns, patientId, onIdentity])

  const selectPatient = () => {
    if (selectedPatientIndex === undefined || !vitaleCardPatients?.[selectedPatientIndex]) return
    onSelectPatient?.({ ...vitaleCardPatients[selectedPatientIndex], insIdentity: patientInsMap[selectedPatientIndex] ?? null })
  }

  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(submitHandler)}>
        <Stack justifyContent="center" spacing={3}>
          {cardChanged && (
            <Alert severity="warning">
              <Trans>The inserted card has been changed</Trans>
            </Alert>
          )}
          {noCardInserted && (
            <Alert severity="warning">
              <Trans>No card inserted</Trans>
            </Alert>
          )}
          <div className="flex flex-col gap-4">
            <SelectCardReader disabled={noCardInserted} newCardInserted={newCardInserted} />
            <div className="flex justify-center">
              <LoadingButton loading={isSubmitting} disabled={noCardInserted} variant="contained" type="submit">
                <Trans>Read vitale card</Trans>
              </LoadingButton>
            </div>
          </div>
          {patientOptions.length > 0 && !isSubmitting && (
            <div className="flex flex-col gap-2">
              <RadioGroupInput
                onChange={(event) => setSelectedPatientIndex(Number(event.target.value))}
                fullWidth
                options={patientOptions}
                value={selectedPatientIndex?.toString() ?? ''}
                variant="contained"
                sx={{ '& .MuiFormControlLabel-label': { flex: 1 } }}
              />
              <div>
                <AlertsPortal area={DMP_CONNECT_VITALE_CARD_FORM_ALERTS} />
              </div>
              <Stack direction="row" justifyContent="center">
                <Button onClick={selectPatient} variant="contained">
                  <Trans>Select</Trans>
                </Button>
              </Stack>
            </div>
          )}
        </Stack>
      </form>
    </FormProvider>
  )
}
